import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import { ContactSection } from "../../components";
import SEOHead from "../../components/SEO/SEOHead";
import { useParams, Navigate } from "react-router-dom";
import { SERVICES_DATA } from "../../data/servicesData";
import {
  generateOrganizationSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
} from "../../utils/structuredData";
import styleVariables from "../../variables.module.scss";

const ServicePage: React.FC = () => {
  const { serviceSlug } = useParams<{ serviceSlug: string }>();
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const rect = parallaxRef.current.getBoundingClientRect();
        const elementTop = rect.top;
        const elementHeight = rect.height;
        const windowHeight = window.innerHeight;

        // Calculate how much of the element is visible
        const elementCenter = elementTop + elementHeight / 2;
        const windowCenter = windowHeight / 2;

        // Calculate parallax offset based on element position relative to viewport
        const parallaxOffset = (elementCenter - windowCenter) * 0.08;
        setOffset(parallaxOffset);
      }
    };

    // Initial calculation
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!serviceSlug || !SERVICES_DATA[serviceSlug]) {
    return <Navigate to="/services" replace />;
  }

  const serviceData = SERVICES_DATA[serviceSlug];

  // Generate SEO data
  const organizationSchema = generateOrganizationSchema();
  const serviceSchema = generateServiceSchema(serviceData);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://brinshandymanservices.ca" },
    { name: "Services", url: "https://brinshandymanservices.ca/services" },
    {
      name: serviceData.name,
      url: `https://brinshandymanservices.ca/service/${serviceSlug}`,
    },
  ]);

  return (
    <div>
      <SEOHead
        title={`${serviceData.name} Services Ottawa - Brin's Handyman Services`}
        description={`Professional ${serviceData.name.toLowerCase()} services in Ottawa and surrounding areas. ${
          serviceData.slug === "plumbing-and-pipes"
            ? "For complex plumbing work requiring licensed professionals, we collaborate with certified plumbers to ensure compliance with local codes and safety regulations. "
            : ""
        }Quality workmanship and customer satisfaction guaranteed.`}
        canonical={`/service/${serviceSlug}`}
        image={serviceData.mainImageUrl}
        type="service"
        structuredData={[organizationSchema, serviceSchema, breadcrumbSchema]}
      />
      <Box
        ref={parallaxRef}
        sx={{
          position: "relative",
          height: "60vh",
          minHeight: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-15%",
            left: 0,
            width: "100%",
            height: "130%",
            backgroundImage: `url(${serviceData.mainImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            transform: `translateY(${offset}px)`,
            willChange: "transform",
            transition: "transform 0.3s ease-out",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            color: "white",
            maxWidth: "800px",
            px: 3,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            {serviceData.name}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              opacity: 0.9,
            }}
          >
            Professional {serviceData.name.toLowerCase()} services in Ottawa and
            surrounding areas
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.8,
            }}
          >
            Quality workmanship guaranteed
          </Typography>
        </Box>
      </Box>

      <Box sx={{ py: 8, backgroundColor: "#f8f9fa" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                color: styleVariables.themeOrangeColor,
                fontWeight: "bold",
                mb: 3,
              }}
            >
              About Our {serviceData.name} Service
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.7,
                mb: 3,
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              {serviceData.description}
            </Typography>
            {serviceData.additionalInfo && (
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  mb: 3,
                  maxWidth: "800px",
                  mx: "auto",
                }}
              >
                {serviceData.additionalInfo}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: 8, backgroundColor: "white" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{
              color: styleVariables.themeGreyColor,
              fontWeight: "bold",
              mb: 4,
            }}
          >
            What We Offer
          </Typography>
          <Grid container spacing={3}>
            {serviceData.features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: styleVariables.themeOrangeColor,
                    borderRadius: 2,
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "black",
                      fontWeight: "bold",
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, backgroundColor: styleVariables.themeGreyColor }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  color: styleVariables.themeOrangeColor,
                  fontWeight: "bold",
                  mb: 3,
                }}
              >
                Why Choose Brin's Handyman Services?
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  color: "white",
                  mb: 2,
                }}
              >
                • Licensed and insured professionals (with certified partners
                for specialized work)
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  color: "white",
                  mb: 2,
                }}
              >
                • Competitive pricing with no hidden fees
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  color: "white",
                  mb: 2,
                }}
              >
                • Quality materials and workmanship
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  color: "white",
                  mb: 2,
                }}
              >
                • Estimates and consultations
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                  color: "white",
                }}
              >
                • Satisfaction guaranteed
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: styleVariables.themeOrangeColor,
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  Ready to Get Started?
                </Typography>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    color: "white",
                    mb: 3,
                  }}
                >
                  Contact us today for an estimate on your{" "}
                  {serviceData.name.toLowerCase()} project.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <ContactSection />
    </div>
  );
};

export default ServicePage;
